/**
 * A labeler's read-only view of QA feedback on their own Coverage Beta
 * submission, opened from the profile page's QA Review section. Renders the
 * completed `COVERAGE_REVIEW_BETA_QA` task the same way AdminCoverageBetaQaTask
 * does (the reviewer's per-box verdicts and task corrections, all locked),
 * and marks it reviewed for the labeler on open.
 */
import { mvpApiGetJobTaskOptions } from "@labeling-tools/api/lebron/generated/@tanstack/react-query.gen";
import { useFetchParcel } from "@labeling-tools/api/lebron/react-query-hooks";
import { Button, LoadingOverlay, Text } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { HEADER_HEIGHT } from "@/config/data";
import { useMarkQaTaskReviewed } from "@/hooks/useMarkQaTaskReviewed";
import { CoverageBetaTask } from "./CoverageBetaTask";

export const CoverageBetaQaFeedback = () => {
  const navigate = useNavigate();
  const job_id = useParams()?.jobId ?? "";
  const [searchParams] = useSearchParams();
  const task_id = searchParams.get("taskId") ?? "";

  const { data: task, isLoading: taskLoading } = useQuery({
    ...mvpApiGetJobTaskOptions({ path: { job_id, task_id } }),
    enabled: !!job_id && !!task_id,
  });

  const parcelId = (task?.data as any)?.parcel_id as string | undefined;
  const { data: parcel, isLoading: parcelLoading } = useFetchParcel(parcelId);

  // Only stamp once the feedback can actually be shown; the stamp is
  // irreversible, so a task that fails to load must stay in Needs Review.
  const canShow = !!task && !!parcel;
  useMarkQaTaskReviewed(canShow ? task : undefined);

  if (taskLoading || parcelLoading) {
    return <LoadingOverlay visible={true} />;
  }

  return (
    <div>
      {/* Sits where AdminToolbar does; CoverageBetaTask leaves room for it
          whenever it's given a task to show read-only. */}
      <div
        className="flex flex-row items-center gap-4 w-full bg-white border-b border-gray-200 px-4 fixed left-0 right-0 z-10"
        style={{ top: HEADER_HEIGHT, height: HEADER_HEIGHT }}
      >
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate("/")}
        >
          Back to profile
        </Button>
        <Text fw={600}>QA feedback</Text>
        <Text size="sm" c="dimmed">
          Read-only: what the QA reviewer marked on your submission
        </Text>
      </div>

      {canShow ? (
        <CoverageBetaTask
          key={task.id}
          adminTask={task}
          adminResult={task.result}
          qaMode
        />
      ) : (
        <Text c="dimmed" ta="center" pt={HEADER_HEIGHT * 2 + 32}>
          Couldn&apos;t load this feedback (task {task_id}). Please report it.
        </Text>
      )}
    </div>
  );
};
